import { useState, useEffect } from 'react';
import { tefApi } from '../../lib/api';
import { validarRutChileno } from '../../lib/rut';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';
import { toast } from '../../hooks/use-toast';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';

const EMPTY = { codigo_cmf: '', descripcion: '', rut_banco_beneficiario: '' };

/** Muestra un RUT (guardado en formato de 10 caracteres) en forma legible:
 *  '0970040005' → '97.004.000-5' · '076645030K' → '76.645.030-K'. */
function rutLegible(rut) {
  const limpio = String(rut || '').replace(/[^0-9kK]/g, '').toUpperCase();
  if (limpio.length < 2) return String(rut || '');
  const dv = limpio.slice(-1);
  const cuerpo = (limpio.slice(0, -1).replace(/^0+/, '') || '0');
  const conPuntos = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${conPuntos}-${dv}`;
}

/** Tabla de bancos por código CMF — colección tef_bancos. */
export function TefBancosTab() {
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // {codigo_cmf, descripcion, rut_banco_beneficiario, _isNew}
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await tefApi.listBancos();
      setBancos(res.data.bancos || []);
    } catch {
      toast({ title: 'Error al cargar bancos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const abrirNuevo = () => setForm({ ...EMPTY, _isNew: true });
  const abrirEditar = (b) => setForm({ ...b, codigo_cmf_original: b.codigo_cmf, rut_banco_beneficiario: rutLegible(b.rut_banco_beneficiario), _isNew: false });

  const rutRaw = (form?.rut_banco_beneficiario || '').toString().trim();
  const rutInvalido = rutRaw.length > 0 && !validarRutChileno(rutRaw);

  const guardar = async () => {
    if (!form.codigo_cmf.toString().trim() || !form.descripcion.trim() || !rutRaw) {
      toast({ title: 'Todos los campos son obligatorios', variant: 'destructive' });
      return;
    }
    if (!validarRutChileno(rutRaw)) {
      toast({ title: 'RUT del banco inválido', description: 'El dígito verificador no cuadra (módulo 11).', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        codigo_cmf: form.codigo_cmf.toString().trim(),
        descripcion: form.descripcion.trim(),
        rut_banco_beneficiario: rutRaw,
      };
      if (form._isNew) await tefApi.crearBanco(payload);
      else await tefApi.actualizarBanco(form.codigo_cmf_original ?? form.codigo_cmf, payload);
      toast({ title: form._isNew ? 'Banco creado' : 'Banco actualizado' });
      setForm(null);
      load();
    } catch (e) {
      toast({ title: 'Error al guardar', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async () => {
    try {
      await tefApi.eliminarBanco(delTarget.codigo_cmf);
      toast({ title: `Banco CMF ${delTarget.codigo_cmf} eliminado` });
      setDelTarget(null);
      load();
    } catch (e) {
      toast({ title: 'Error al eliminar', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{bancos.length} banco(s)</p>
          <Button size="sm" onClick={abrirNuevo} className="gap-1.5" data-testid="tef-banco-nuevo">
            <Plus className="h-4 w-4" /> Nuevo banco
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        ) : (
          <Table data-testid="tef-bancos-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Código CMF</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead className="w-40">RUT banco</TableHead>
                <TableHead className="w-24 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bancos.map((b) => (
                <TableRow key={b.codigo_cmf} data-testid={`tef-banco-row-${b.codigo_cmf}`}>
                  <TableCell className="font-mono">{b.codigo_cmf}</TableCell>
                  <TableCell>{b.descripcion}</TableCell>
                  <TableCell className="font-mono">{rutLegible(b.rut_banco_beneficiario)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => abrirEditar(b)} data-testid={`tef-banco-edit-${b.codigo_cmf}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => setDelTarget(b)} data-testid={`tef-banco-del-${b.codigo_cmf}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {bancos.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Sin bancos.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Dialog crear/editar */}
      <Dialog open={!!form} onOpenChange={(o) => !o && !saving && setForm(null)}>
        <DialogContent data-testid="tef-banco-dialog">
          <DialogHeader>
            <DialogTitle>{form?._isNew ? 'Nuevo banco' : `Editar banco CMF ${form?.codigo_cmf}`}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Código CMF</Label>
                <Input
                  value={form.codigo_cmf}
                  onChange={(e) => setForm({ ...form, codigo_cmf: e.target.value })}
                  placeholder="1"
                  data-testid="tef-banco-cmf-input"
                />
                {!form._isNew && (
                  <p className="text-[11px] text-muted-foreground">
                    El código lo asigna la CMF y puede cambiar. Si lo modificas, las cuentas de
                    proveedores enlazadas se actualizan automáticamente.
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Descripción</Label>
                <Input
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  placeholder="Banco de Chile / A. Edwards / Citibank N.A."
                  data-testid="tef-banco-desc-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>RUT banco beneficiario</Label>
                <Input
                  value={form.rut_banco_beneficiario}
                  onChange={(e) => setForm({ ...form, rut_banco_beneficiario: e.target.value })}
                  placeholder="97.004.000-5"
                  className={rutInvalido ? 'border-red-500 focus-visible:ring-red-500' : ''}
                  aria-invalid={rutInvalido}
                  data-testid="tef-banco-rut-input"
                />
                {rutInvalido ? (
                  <p className="text-[11px] text-red-600" data-testid="tef-banco-rut-error">
                    RUT inválido: el dígito verificador no cuadra (módulo 11).
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    Se guarda en formato de 10 caracteres del Banco de Chile (ej.
                    97.004.000-5 → 0970040005).
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)} disabled={saving}>Cancelar</Button>
            <Button onClick={guardar} disabled={saving || rutInvalido} className="gap-1.5" data-testid="tef-banco-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar borrado */}
      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="tef-banco-del-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar banco</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar el banco CMF {delTarget?.codigo_cmf} ({delTarget?.descripcion})? Las cuentas
              que lo usen quedarán sin banco válido y sus facturas se omitirán al generar el TEF.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminar} className="bg-red-600 hover:bg-red-700" data-testid="tef-banco-del-confirm">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
